import {
  Box,
  Button,
  Center,
  PasswordInput,
  Stack,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import type { CustomNextPage } from "next";
// import { useRouter } from "next/router";
import { getSession, signIn } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { useState, type ComponentProps } from "react";
import { DeletedAccountMessageModal } from "src/component/element/DeletedAccountMessage/DeletedAccountMessageModal";
import { AuthLayout } from "src/layout";
import { errorShowNotification } from "src/lib/mantine";
import { usePostMissionCountMutation } from "src/service/MissionApi";
import { useRecordUserEventMutation } from "src/service/UserEventsApi/UserEventsApi";
import type { ResponseUser } from "src/types/nextAuth";
import { z } from "zod";

import styles from "./index.module.scss";

const schema = z.object({
  email: z.string().email({ message: "メールアドレスを入力してください。" }),
  password: z
    .string()
    .min(8, "パスワードは8文字以上で入力してください")
    .regex(
      /^(?=.*?[a-z])(?=.*?\d)[a-z\d]{8,100}$/i,
      "パスワードは半角英数字混合で入力してください",
    ),
  role: z.string(),
});

const SignIn: CustomNextPage = () => {
  // const router = useRouter();
  const [deletedAccountInfo, setDeletedAccountInfo] = useState<ResponseUser>();
  const [record] = useRecordUserEventMutation();
  const [postMissionCount] = usePostMissionCountMutation();
  // const [getUserSettings]=useGetUserSettingsMutation()
  const PageCount = async (action: string) => {
    await postMissionCount({ actionId: action }).unwrap();
  };
  const recordEventLogin = async () => {
    await record({
      action: "CLICK",
      eventType: "LOGIN",
      category: "ユーザーとの対話",
      label: "ログインボタン",
      value: "1",
    }).unwrap();
  };
  const form = useForm({
    initialValues: {
      email: "",
      password: "",
      role: "",
    },
    validate: zodResolver(schema),
  });

  const handleSubmit: ComponentProps<"form">["onSubmit"] = async (e) => {
    e.preventDefault();
    await signIn("credentials", {
      email: form.values.email,
      password: form.values.password,
      redirect: false,
      // role: "STUDENT",
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
    }).then(async ({ ok, error }) => {
      if (error) {
        errorShowNotification(error);
        return;
      }
      if (ok) {
        const session = await getSession();
        const isDeleted = session?.user?.user.isDeleted;
        if (isDeleted) {
          setDeletedAccountInfo(session?.user);
          return;
        }

        PageCount("LOGIN");
        recordEventLogin();

        location.href = `${process.env.NEXT_PUBLIC_APP_URL}/mypage`;
      } else {
        errorShowNotification("メールアドレスとパスワードが一致しません。");
      }
    });
  };
  return (
    <div>
      <Image
        src="/images/logo.svg"
        alt="TANQ BASE"
        width={200}
        height={100}
        className={styles.logo}
      />
      <Title mt={24} size={18}>
        ログイン
      </Title>
      <Stack mt={24}>
        <form onSubmit={handleSubmit}>
          <Box>
            <label htmlFor="email">メールアドレス</label>
            <TextInput
              type="email"
              id="email"
              name="email"
              placeholder="example@example.com"
              withAsterisk
              {...form.getInputProps("email")}
            />
          </Box>
          <Box mt={16}>
            <label htmlFor="password">パスワード</label>
            <PasswordInput
              id="password"
              name="password"
              placeholder="パスワード"
              {...form.getInputProps("password")}
            />
          </Box>
          <Center mt={24}>
            <Button
              type="submit"
              sx={{
                borderRadius: "100px",
                height: "auto",
                maxWidth: "256px",
                padding: "14.5px 0",
                width: "100%",
              }}
            >
              ログイン
            </Button>
          </Center>
        </form>
      </Stack>
      <Box mt={48}>
        <Link
          href="/auth/signup"
          style={{ color: "#00A5B9", textDecoration: "underline" }}
        >
          アカウント作成
        </Link>
      </Box>
      <Box mt={24}>
        <Link
          href="/password_reset_request"
          style={{ color: "#00A5B9", textDecoration: "underline" }}
        >
          パスワードを忘れた方はこちら
        </Link>
      </Box>
      {deletedAccountInfo && (
        <DeletedAccountMessageModal
          reactiveToken={deletedAccountInfo.accessKey?.value}
          deletedAt={deletedAccountInfo.user.deletedAt}
          onClose={() => setDeletedAccountInfo(undefined)}
        />
      )}
    </div>
  );
};

SignIn.getLayout = AuthLayout;

export default SignIn;
